import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { UseFormWatch, UseFormSetValue, UseFieldArrayReturn, FieldErrors } from "react-hook-form"
import { numberValue, toNumber } from "../shared/numberCoercion"
import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { CollapsibleSection } from "./CollapsibleSection"
import { DataQualityFormData } from "./types"

/**
 * Props for the PreConformanceSection component.
 */
interface PreConformanceSectionProps {
  /**
   * React-hook-form watch function for observing field values.
   */
  watch: UseFormWatch<DataQualityFormData>
  /**
   * React-hook-form setValue function for programmatic updates.
   */
  setValue: UseFormSetValue<DataQualityFormData>
  /**
   * Field array controller for file rules (from useFieldArray)
   */
  fileFields: UseFieldArrayReturn<DataQualityFormData, "preConformance.files">
  /**
   * Whether the form is in edit mode.
   */
  canEdit: boolean
  /**
   * Form validation errors.
   */
  errors?: FieldErrors<DataQualityFormData>
}

/**
 * Section component for pre-conformance validation rules. Includes upload format settings, zip file
 * rules, file count constraints, and individual file validation rules.
 */
export const PreConformanceSection: React.FC<PreConformanceSectionProps> = ({
  watch,
  setValue,
  fileFields,
  canEdit,
  errors,
}) => {
  return (
    <CollapsibleSection
      title="Pre-Conformance Rules"
      description="Quick checks before data processing begins"
      commentAnchor="Data Quality Rules / Pre-Conformance"
    >
      <SpaceBetween size="l">
        <SpaceBetween size="xs">
          {canEdit ? (
            <Checkbox
              checked={!!watch("preConformance.uploadFormat.allowZip")}
              onChange={({ detail }) =>
                setValue("preConformance.uploadFormat.allowZip", detail.checked, {
                  shouldDirty: true,
                })
              }
              description="Uploaded file can be a .zip archive"
            >
              Allow Zip
            </Checkbox>
          ) : (
            <FormField label="Allow Zip">
              <ReadOnlyValue value={watch("preConformance.uploadFormat.allowZip") ? "Yes" : "No"} />
            </FormField>
          )}
          {canEdit ? (
            <Checkbox
              checked={!!watch("preConformance.uploadFormat.allowUnzipped")}
              onChange={({ detail }) =>
                setValue("preConformance.uploadFormat.allowUnzipped", detail.checked, {
                  shouldDirty: true,
                })
              }
              description="Uploaded file can be a single uncompressed file"
            >
              Allow Unzipped
            </Checkbox>
          ) : (
            <FormField label="Allow Unzipped">
              <ReadOnlyValue
                value={watch("preConformance.uploadFormat.allowUnzipped") ? "Yes" : "No"}
              />
            </FormField>
          )}
          {canEdit ? (
            <Checkbox
              checked={!!watch("preConformance.uploadFormat.requireZip")}
              onChange={({ detail }) =>
                setValue("preConformance.uploadFormat.requireZip", detail.checked, {
                  shouldDirty: true,
                })
              }
              description="Uploaded file must be a .zip archive"
            >
              Require Zip
            </Checkbox>
          ) : (
            <FormField label="Require Zip">
              <ReadOnlyValue
                value={watch("preConformance.uploadFormat.requireZip") ? "Yes" : "No"}
              />
            </FormField>
          )}
        </SpaceBetween>

        <ColumnLayout columns={2} borders="none">
          <FormField
            label="Zip Name Regex"
            description={
              errors?.preConformance?.zipFile?.nameRegex
                ? undefined
                : "Uploaded zip filename must match this pattern"
            }
            errorText={errors?.preConformance?.zipFile?.nameRegex?.message}
          >
            {canEdit ? (
              <Input
                type="text"
                placeholder="^STUDY-\d{4}-.*\.zip$"
                value={watch("preConformance.zipFile.nameRegex") ?? ""}
                onChange={({ detail }) =>
                  setValue("preConformance.zipFile.nameRegex", detail.value, { shouldDirty: true })
                }
                invalid={!!errors?.preConformance?.zipFile?.nameRegex}
              />
            ) : (
              <ReadOnlyValue value={watch("preConformance.zipFile.nameRegex")} />
            )}
          </FormField>
          <FormField
            label="Zip Max Length"
            description={
              errors?.preConformance?.zipFile?.maxLength
                ? undefined
                : "Uploaded zip filename cannot exceed this length"
            }
            errorText={errors?.preConformance?.zipFile?.maxLength?.message}
          >
            {canEdit ? (
              <Input
                type="number"
                value={numberValue(watch("preConformance.zipFile.maxLength"))}
                onChange={({ detail }) =>
                  setValue("preConformance.zipFile.maxLength", toNumber(detail.value), {
                    shouldDirty: true,
                  })
                }
                invalid={!!errors?.preConformance?.zipFile?.maxLength}
                controlId="preConformance.zipFile.maxLength"
              />
            ) : (
              <ReadOnlyValue value={numberValue(watch("preConformance.zipFile.maxLength"))} />
            )}
          </FormField>
        </ColumnLayout>

        {canEdit ? (
          <Checkbox
            checked={!!watch("preConformance.zipFile.validateStudyId")}
            onChange={({ detail }) =>
              setValue("preConformance.zipFile.validateStudyId", detail.checked, {
                shouldDirty: true,
              })
            }
            description="Uploaded zip filename must contain the study ID"
          >
            Validate Study ID in Zip Filename
          </Checkbox>
        ) : (
          <FormField label="Validate Study ID in Zip Filename">
            <ReadOnlyValue value={watch("preConformance.zipFile.validateStudyId") ? "Yes" : "No"} />
          </FormField>
        )}

        <ColumnLayout columns={2} borders="none">
          <FormField label="Min Files" description="Upload must contain at least this many files">
            {canEdit ? (
              <Input
                type="number"
                value={numberValue(watch("preConformance.fileCount.min"))}
                onChange={({ detail }) =>
                  setValue("preConformance.fileCount.min", toNumber(detail.value), {
                    shouldDirty: true,
                  })
                }
              />
            ) : (
              <ReadOnlyValue value={numberValue(watch("preConformance.fileCount.min"))} />
            )}
          </FormField>
          <FormField
            label="Max Files"
            description="Upload cannot contain more than this many files"
          >
            {canEdit ? (
              <Input
                type="number"
                value={numberValue(watch("preConformance.fileCount.max"))}
                onChange={({ detail }) =>
                  setValue("preConformance.fileCount.max", toNumber(detail.value), {
                    shouldDirty: true,
                  })
                }
              />
            ) : (
              <ReadOnlyValue value={numberValue(watch("preConformance.fileCount.max"))} />
            )}
          </FormField>
        </ColumnLayout>

        <SpaceBetween size="s">
          <Box>
            <Box variant="h5" padding="n">
              File Rules
            </Box>
            <Box variant="small" color="text-body-secondary">
              Validation rules for individual files within the upload
            </Box>
          </Box>
          {fileFields.fields.map((field, i) => (
            <Container
              key={field.id}
              header={
                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                  <Box variant="strong">File Rule {i + 1}</Box>
                  {canEdit && (
                    <Button variant="inline-link" onClick={() => fileFields.remove(i)}>
                      Remove
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              <SpaceBetween size="s">
                <ColumnLayout columns={2} borders="none">
                  <FormField
                    label="Name"
                    description={
                      errors?.preConformance?.files?.[i]?.name
                        ? undefined
                        : "Label for this file rule"
                    }
                    errorText={errors?.preConformance?.files?.[i]?.name?.message}
                  >
                    {canEdit ? (
                      <Input
                        type="text"
                        placeholder="primary_data"
                        value={watch(`preConformance.files.${i}.name`) ?? ""}
                        onChange={({ detail }) =>
                          setValue(`preConformance.files.${i}.name`, detail.value, {
                            shouldDirty: true,
                          })
                        }
                        invalid={!!errors?.preConformance?.files?.[i]?.name}
                      />
                    ) : (
                      <ReadOnlyValue value={watch(`preConformance.files.${i}.name`)} />
                    )}
                  </FormField>
                  <FormField
                    label="Name Regex"
                    description={
                      errors?.preConformance?.files?.[i]?.nameRegex
                        ? undefined
                        : "Filename must match this pattern"
                    }
                    errorText={errors?.preConformance?.files?.[i]?.nameRegex?.message}
                  >
                    {canEdit ? (
                      <Input
                        type="text"
                        placeholder="^AE_.*\.csv$"
                        value={watch(`preConformance.files.${i}.nameRegex`) ?? ""}
                        onChange={({ detail }) =>
                          setValue(`preConformance.files.${i}.nameRegex`, detail.value, {
                            shouldDirty: true,
                          })
                        }
                        invalid={!!errors?.preConformance?.files?.[i]?.nameRegex}
                      />
                    ) : (
                      <ReadOnlyValue value={watch(`preConformance.files.${i}.nameRegex`)} />
                    )}
                  </FormField>
                  <FormField
                    label="Extensions (comma-sep)"
                    description={
                      errors?.preConformance?.files?.[i]?.allowedExtensions
                        ? undefined
                        : "File must have one of these extensions"
                    }
                    errorText={errors?.preConformance?.files?.[i]?.allowedExtensions?.message}
                  >
                    {canEdit ? (
                      <Input
                        type="text"
                        placeholder=".csv, .sas7bdat"
                        value={watch(`preConformance.files.${i}.allowedExtensions`) ?? ""}
                        onChange={({ detail }) =>
                          setValue(`preConformance.files.${i}.allowedExtensions`, detail.value, {
                            shouldDirty: true,
                          })
                        }
                        invalid={!!errors?.preConformance?.files?.[i]?.allowedExtensions}
                      />
                    ) : (
                      <ReadOnlyValue value={watch(`preConformance.files.${i}.allowedExtensions`)} />
                    )}
                  </FormField>
                  <FormField
                    label="Max Length"
                    description={
                      errors?.preConformance?.files?.[i]?.maxLength
                        ? undefined
                        : "Filename cannot exceed this length"
                    }
                    errorText={errors?.preConformance?.files?.[i]?.maxLength?.message}
                  >
                    {canEdit ? (
                      <Input
                        type="number"
                        value={numberValue(watch(`preConformance.files.${i}.maxLength`))}
                        onChange={({ detail }) =>
                          setValue(`preConformance.files.${i}.maxLength`, toNumber(detail.value), {
                            shouldDirty: true,
                          })
                        }
                        invalid={!!errors?.preConformance?.files?.[i]?.maxLength}
                      />
                    ) : (
                      <ReadOnlyValue
                        value={numberValue(watch(`preConformance.files.${i}.maxLength`))}
                      />
                    )}
                  </FormField>
                </ColumnLayout>
                <SpaceBetween size="xs">
                  {canEdit ? (
                    <Checkbox
                      checked={!!watch(`preConformance.files.${i}.required`)}
                      onChange={({ detail }) =>
                        setValue(`preConformance.files.${i}.required`, detail.checked, {
                          shouldDirty: true,
                        })
                      }
                      description="Upload must include a file matching this rule"
                    >
                      Required
                    </Checkbox>
                  ) : (
                    <FormField label="Required">
                      <ReadOnlyValue
                        value={watch(`preConformance.files.${i}.required`) ? "Yes" : "No"}
                      />
                    </FormField>
                  )}
                  {canEdit ? (
                    <Checkbox
                      checked={!!watch(`preConformance.files.${i}.validateStudyId`)}
                      onChange={({ detail }) =>
                        setValue(`preConformance.files.${i}.validateStudyId`, detail.checked, {
                          shouldDirty: true,
                        })
                      }
                      description="Filename must contain the study ID"
                    >
                      Validate Study ID
                    </Checkbox>
                  ) : (
                    <FormField label="Validate Study ID">
                      <ReadOnlyValue
                        value={watch(`preConformance.files.${i}.validateStudyId`) ? "Yes" : "No"}
                      />
                    </FormField>
                  )}
                  {canEdit ? (
                    <Checkbox
                      checked={!!watch(`preConformance.files.${i}.noTimestampWhenZipped`)}
                      onChange={({ detail }) =>
                        setValue(
                          `preConformance.files.${i}.noTimestampWhenZipped`,
                          detail.checked,
                          {
                            shouldDirty: true,
                          },
                        )
                      }
                      description="Reject files with timestamps when inside a zip"
                    >
                      No Timestamp When Zipped
                    </Checkbox>
                  ) : (
                    <FormField label="No Timestamp When Zipped">
                      <ReadOnlyValue
                        value={
                          watch(`preConformance.files.${i}.noTimestampWhenZipped`) ? "Yes" : "No"
                        }
                      />
                    </FormField>
                  )}
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          ))}
          {canEdit && (
            <Button
              variant="inline-link"
              iconName="add-plus"
              onClick={() =>
                fileFields.append({
                  name: "",
                  nameRegex: "",
                  maxLength: 40,
                  allowedExtensions: "",
                  required: false,
                  validateStudyId: false,
                })
              }
            >
              Add File Rule
            </Button>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </CollapsibleSection>
  )
}
